import React from 'react';
import { Box, Radio as NBRadio, themeTools } from 'native-base';
import PropTypes from 'prop-types';
import styleShape from 'react-style-proptype';
import { WithColorMode } from '~/native-ui/hoc';
import { FormControl } from '~/native-ui/FormControl';
import { extractFlexProps, extractSpacingProps } from '~/native-ui/utils';

/**
 * 
 * @typedef {import('native-base').IRadioProps} IRadioProps
 * @param {React.ComponentType<IRadioProps>} RadioButton 
 * @returns {React.ComponentType<object>}
 */
const factory = (RadioButton) => {
    class Radio extends React.Component {
        static propTypes = {
            checked: PropTypes.bool,
            children: PropTypes.node,
            className: PropTypes.string,
            disabled: PropTypes.bool,
            readonly: PropTypes.bool,
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
            name: PropTypes.string,
            onChange: PropTypes.func,
            onMouseEnter: PropTypes.func,
            onMouseLeave: PropTypes.func,
            style: styleShape,
            theme: PropTypes.shape({
                disabled: PropTypes.string,
                field: PropTypes.string,
                input: PropTypes.string,
                ripple: PropTypes.string,
            }),
        };

        static defaultProps = {
            checked: false,
            className: '',
            disabled: false,
        };

        handleToggle = (isChecked, event) => {
            if (!this.props.disabled && this.props.onChange) {
                this.props.onChange(!this.props.checked, event);
            }
        };

        renderRadioLabel() {
            const { label, tooltip } = this.props;
            return label ? (
                <FormControl.Label ml="2" tooltip={tooltip}>
                    {label}
                </FormControl.Label>
            ) : null;
        }

        renderRadioDescription() {
            const { children } = this.props;
            return children ? (
                <Box flex="1">
                    <Box width="100%" ml="2">
                        {children}
                    </Box>
                </Box>
            ) : null;
        }

        render() {
            const { value, disabled, readonly } = this.props;

            const radioProps = {
                value,
                isDisabled: readonly || disabled,
                _interactionBox: readonly ? { display: 'none' } : undefined,
                _checked: readonly ? { bg: themeTools.mode(`muted.50`, `muted.800`)(this.props) } : undefined,
                _icon: readonly ? { color: 'primary.500' } : undefined,
            };

            return (
                <FormControl>
                    <RadioButton {...radioProps} accessibilityLabel={typeof this.props.label === 'string' ? this.props.label  ?? ' ' : ' '}>{this.renderRadioLabel()}</RadioButton>
                </FormControl>
            );
        }
    }

    return WithColorMode(Radio);
};

const Radio = React.memo(factory(NBRadio));
export { factory as radioFactory };
export default Radio;
export { Radio };

export const RadioGroup = React.memo(
    React.forwardRef(function RadioGroup({ label, isInvalid, ...props }, ref) {
        const [spaceProps, notSpaceProps] = extractSpacingProps(props);
        const [flexProps, radioProps] = extractFlexProps(notSpaceProps);

        return (
            <FormControl {...spaceProps} {...flexProps} ref={ref} accessibilityLabel=" ">
                {label && <FormControl.Label label={label} color={isInvalid ? "danger.900" : undefined} />}
                <NBRadio.Group {...radioProps} flexDirection="row" accessibilityLabel={typeof label === "string" ? label ?? " " : ' '} />
            </FormControl>
        );
    })
);
